import { orderBy } from "@progress/kendo-data-query";

import { specialMACSorting } from "../../scenes/Facility/utils";
import { formatZipCode } from "../../scenes/Practice/utils";
import { get, post } from "../../utils/net";
import { facilityToIdDescriptionBaseMapper } from "../choiceLists";

import type { BackgroundJobResponse, FacilitySyncJobResult } from "../../models/backgroundJob";
import type { FacilityPreferences } from "../../models/facilitySettings";
import type { IdDescriptionBase } from "../../models/patientEncounter";
import type { MACDropDownRow } from "../../scenes/Facility/models/facilities";
import type {
  PracticeEntity,
  ValidateZIPCodeResponse,
} from "../../scenes/Practice/models/practices";

const baseURL = process.env.REACT_APP_API_PATH;
const mneURL = process.env.REACT_APP_TEE_MNE;

const mapPracticeResultToEntity = (practice: PracticeEntity): PracticeEntity => ({
  ...practice,
  Id: practice.Id ?? "",
  ZipCode: formatZipCode(practice.ZipCode),
  PracticeType: practice.PracticeType ?? "",
  Description: practice.Description ?? "",
  Mac: practice.Mac ?? "",
});

const mapPracticesResultToEntity = (result: PracticeEntity[]): PracticeEntity[] => {
  if (!result.length) {
    return [];
  }
  return result.map((practice) => mapPracticeResultToEntity(practice));
};

const listPractices = async (): Promise<PracticeEntity[]> => {
  const url = `${baseURL}/api/Practices`;
  const extraOptions = { useExactPath: true };

  const result = await get(url, extraOptions);
  return mapPracticesResultToEntity(result.body as PracticeEntity[]);
};

const getPracticeListDropDown = async (): Promise<IdDescriptionBase[]> => {
  const url = `${baseURL}/api/ChoiceLists/practices`;
  const extraOptions = { useExactPath: true };

  const result = await get(url, extraOptions);

  const items = (result.body ?? []) as FacilityPreferences[];
  const facilities: IdDescriptionBase[] = items.map(facilityToIdDescriptionBaseMapper);

  return orderBy(facilities, [{ field: "id", dir: "asc" }]);
};

const getMACDropDown = async (): Promise<MACDropDownRow[]> => {
  const url = `${mneURL}/MedicalNecessityEdits/FiscalIntermediaries`;
  const extraOptions = { useExactPath: true };

  const result = await get(url, extraOptions);
  const unsorted = result.body as MACDropDownRow[];
  const sorted = specialMACSorting(unsorted, [{ field: "Mac", dir: "asc" }]);
  return sorted as MACDropDownRow[];
};

const createPractice = async (practice: PracticeEntity): Promise<PracticeEntity> => {
  const url = `${baseURL}/api/Practices`;
  const extraOptions = { useExactPath: true };

  const result = await post(url, practice, extraOptions);
  return mapPracticeResultToEntity(result.body as PracticeEntity);
};

const updatePractice = async (practice: PracticeEntity): Promise<PracticeEntity> => {
  const url = `${baseURL}/api/Practices`;
  const extraOptions = { useExactPath: true };

  const result = await post(url, practice, extraOptions);
  return mapPracticeResultToEntity(result.body as PracticeEntity);
};

const getPractice = async (practiceId: string): Promise<PracticeEntity> => {
  const url = `${baseURL}/api/Practices/${practiceId}`;
  const extraOptions = { useExactPath: true };

  const result = await get(url, extraOptions);
  return mapPracticeResultToEntity(result.body as PracticeEntity);
};

const validateZIPCode = async (zip: string) => {
  const url = `${mneURL}/MedicalNecessityEdits/ZipCode/${zip}`;
  const extraOptions = { useExactPath: true };

  const response = await get(url, extraOptions);

  return response.body as ValidateZIPCodeResponse;
};

interface PracticeSyncPayload {
  SourceFacilityId: string;
  TargetFacilityIds: string[];
}

const startPracticeSync = (payload: PracticeSyncPayload): Promise<BackgroundJobResponse> => {
  const url = `${baseURL}/api/Practices/sync`;
  const extraOptions = { useExactPath: true };

  return post(url, payload, extraOptions)
    .then((result: { body: BackgroundJobResponse }) => result.body)
    .catch((error: { status: number }) => {
      // Handle 409 Conflict - another sync operation is already in progress
      if (error.status === 409) {
        const conflictError = new Error(
          "Another settings copying operation is already in progress.",
        );
        (conflictError as Error & { status: number }).status = 409;
        throw conflictError;
      }
      throw error;
    });
};

const getPracticeSyncStatus = (): Promise<BackgroundJobResponse<FacilitySyncJobResult>> => {
  const url = `${baseURL}/api/Practices/sync`;
  const extraOptions = { useExactPath: true };

  return get(url, extraOptions).then(
    (result: { body: BackgroundJobResponse<FacilitySyncJobResult> }) => result.body,
  );
};

const getPracticeSyncTargets = (practiceId: string): Promise<string[]> => {
  const url = `${baseURL}/api/Practices/${practiceId}/synctargets`;
  const extraOptions = { useExactPath: true };

  return get(url, extraOptions).then((result: { body: string[] }) => result.body || []);
};

export const practicesAPI = {
  listPractices,
  getPracticeListDropDown,
  getMACDropDown,
  createPractice,
  updatePractice,
  getPractice,
  validateZIPCode,
  startPracticeSync,
  getPracticeSyncStatus,
  getPracticeSyncTargets,
};
